import { notification } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useSecondaryMarketEligibility from './useSecondaryMarketEligibility';
import ListingAgreementNotification from '../components/ListingAgreement/ListingAgreementNotification';
import { getFundingSources } from '../services/AccountApi/paymentMethodApi';
import { resetLoginPin } from '../services/AccountApi/profileApi';
import { getAccountBalances } from '../services/InitialOfferings/initialOfferingSection';
import { addUserData } from '../store/reducer/accountSlice';
import { fetchPropertyDetailsAction, updatePropertyDetails } from '../store/reducer/propertyDetailSlice';
import { secondarySellOrderBid, seondaryPlaceBidOrder } from '../store/reducer/secondaryQuickBuySlice';
import { toastNotify } from '../utils/toastNotify';
import { getUsCurrencyFormat } from '../utils/usCurrencyFormatter';
import { secondaryBuy } from '../constants/secondaryBuy';

const useQuickBuySecondary = (modalState, quickBuyPropertyData, propertyDetailData = {}, { skipEligibilityToast = false } = {}) => {
  const propertyDetails = useSelector((state) => state.propertyDetails.data);
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState('');
  const [walletsData, setWalletsData] = useState([]);
  const [numberOfShare, setNumberOfShare] = useState('');
  const [resetPin, setResetPin] = useState(false);
  const [isTurmAndCondistion, setIsTurmAndCondistion] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({});
  const [isEditPayment, setIsEditPayment] = useState(false);
  const [limitPrice, setLimitPrice] = useState('');
  const [duration, setDuration] = useState('Good Until Cancel');
  const [partialFills, setPartialFills] = useState(true);
  const [activeTab, setActiveTab] = useState('1');
  const [userBalance, setUserBalance] = useState();
  const [selectedProperty, setSelectedProperty] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [storeOrderValue, setStoreOrderValue] = useState();
  const [openPinModal, setOpenPinModal] = useState(false);
  const [isTOSModal, setIsTOSModal] = useState(false);
  const [portfolioTOSModal, setPortfolioTOSModal] = useState(false);
  const [isPinVerified,setIsPinVerified]=useState(false)
  // Flag to skip the eligibility check once — set when the user completes
  // SecondaryMarketAccessModal so the toast is not auto-fired on modal close.
  const skipEligibilityOnce = useRef(false);
  const dispatch = useDispatch();

  let LowestAskPrice = Number(propertyDetails?.data?.lowestAsk);
  let highestBid = Number(propertyDetails?.data?.highestBid);
  let perSharePrice = Number(limitPrice);
  let userPurchasedShare = Number(propertyDetails?.data?.userPurchaseDetail?.noOfShares);


const userData = useSelector((state) => state.userData?.userData);
/**
 * @type {boolean}
 */
const bankExists = useMemo(() => userData.isBankExist, [userData.isBankExist]);

/**
 * @type {boolean}
 */
const bankVerified = useMemo(() => !userData.isUnverifiedBank, [userData.isUnverifiedBank]);

/**
 * @type {string}
 */
const dwollaWalletBalance = useMemo(() => userData.availableBalance, [userData.availableBalance]);

/**
 * @type {boolean}
 */
const dwollaWalletPendingTransfer = useMemo(() => userData.isPendingTransfer, [userData.isPendingTransfer]);

  const totalInvestment = parseFloat((Number(limitPrice) * Number(numberOfShare)).toFixed(2)) || 0;
  const sharesRemainingToPurchase = Math.floor(
    (
      (Number(propertyDetails?.data?.totalShare) * 9.8) / 100 -
      Number(propertyDetails?.data?.userPurchaseDetail?.noOfShares) -
      Number(propertyDetails?.data?.requestedShares)
    ).toFixed(2)
  );

  const isBuyOrdersPending = propertyDetails?.data?.userPurchaseDetail?.isBuyOrdersPending;

  // Only subtract remainingOpenShares when they represent open BUY orders (isBuyOrdersPending === true).
  // If the open orders are SELL orders, they are already counted within the user's owned shares (noOfShares)
  // and should NOT reduce the max buy (9.8%) capacity.
  const sharesRemainingToPurchaseForSecondary = Math.floor(
    (
      (Number(propertyDetails?.data?.totalShare) * 9.8) / 100 -
      Number(propertyDetails?.data?.userPurchaseDetail?.noOfShares) -
      (isBuyOrdersPending ? Number(propertyDetails?.data?.userPurchaseDetail?.remainingOpenShares) : 0)
    ).toFixed(2)
  );
    
  const isMaxRangeError = Number(numberOfShare) > Number(sharesRemainingToPurchaseForSecondary);
  const minButtonValue =
    Number(propertyDetails?.data?.minimumPrice) / limitPrice === Infinity
      ? 0
      : Math.ceil(getUsCurrencyFormat(propertyDetails?.data?.minimumPrice / limitPrice));
  const isMinRangeError = Number(numberOfShare) < Number(minButtonValue);
  const transactionFee = getUsCurrencyFormat((Number(limitPrice) * Number(numberOfShare) * 1) / 100 || 0);
  const totalPurchase =
    activeTab === '1'
      ? Math.round((Number(limitPrice) * Number(numberOfShare) + (Number(limitPrice) * Number(numberOfShare) * 1) / 100 + 2.5) * 100) / 100 || 0
      : (limitPrice && numberOfShare
          ? Math.round((Number(limitPrice) * Number(numberOfShare) - (Number(limitPrice) * Number(numberOfShare) * 1) / 100 - 2.5) * 100) / 100
          : 0) || 0;
  const handleFeatchOrderDetail = async () => {
    await dispatch(
      fetchPropertyDetailsAction({
        id: quickBuyPropertyData?.propertyId,
        onSuccess: () => {},
      })
    );
  };

  const handleFeatchBankDetail = async (value) => {
    try {
      const [fundingData, balanceData] = await Promise.all([getFundingSources(), getAccountBalances()]);
      if (!fundingData.isError) {
        const accountAvailableBalance = !balanceData?.isError ? balanceData?.data?.availableBalance : undefined;
        const updatedWallets = fundingData?.data?.wallets.map((wallet) => ({
          ...wallet,
          ...(accountAvailableBalance !== undefined && { availableBalance: accountAvailableBalance }),
        }));
        setWalletsData(updatedWallets.concat(fundingData?.data?.banks).concat({ id: -1, brandName: 'Add new', sourceType: 'add new' }));
        setSelectedPaymentMethod(updatedWallets[0]);
      } else {
        toastNotify('error', fundingData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPaymentMethod = (value) => {
    let matchedObject = walletsData?.find((item) => item.id === value);
    let updatedWallet = walletsData?.filter((item) => item.id !== value && item.sourceType === 'Wallet');
    let updatedBank = walletsData?.filter((item) => item.id !== value && item.sourceType === 'Bank');
    setWalletsData([matchedObject, ...updatedWallet, ...updatedBank, { id: -1, brandName: 'Add new', sourceType: 'add new' }]);
    setSelectedPaymentMethod(matchedObject);
    setIsEditPayment(false);
  };

  const { isBuyEligible, isSellEligible, buyIneligibleMsg, sellIneligibleMsg } = useSecondaryMarketEligibility();

  useEffect(() => {
    if (!isPinVerified) return;

    // If the Secondary Market Access Modal needs to be shown, open it first.
    // Do NOT show the eligibility toast in this case — the modal will handle the user journey.
    const isSecondaryApplicationRequired = userData?.isSecondaryApplicationRequired;
    if (isSecondaryApplicationRequired === true) {
      modalState('AccessModal');
      setIsPinVerified(false);
      return;
    }

    // SecondaryMarketAccessModal is not going to be shown.
    // If the user just completed the access modal, suppress everything — no toast,
    // no auto-navigation to bank/balance steps. The user must explicitly click
    // Buy/Sell again to continue the flow.
    if (skipEligibilityOnce.current) {
      skipEligibilityOnce.current = false;
      setIsPinVerified(false);
      modalState('');
      return;
    }

    // Enforce eligibility with a blocking toast — unless the caller opts out
    // (skipEligibilityToast), in which case the calling UI surfaces
    // ineligibility itself (e.g. a disabled Buy/Sell tab with a tooltip).
    if (!skipEligibilityToast) {
      if (activeTab === '2') {
        if (!isSellEligible) {
          toastNotify('error', sellIneligibleMsg);
          setIsPinVerified(false);
          modalState('');
          return;
        }
      } else {
        if (!isBuyEligible) {
          toastNotify('error', buyIneligibleMsg);
          setIsPinVerified(false);
          modalState('');
          return;
        }
      }
    }

    if(!bankExists){
      modalState(secondaryBuy.ADD_BANK);
    }else if(!bankVerified){
      modalState(secondaryBuy.VERIFY_BANK);
    }else {
      // Skip wallet balance check for sell orders (activeTab === '2')
      if (activeTab === '2') {
        modalState('StepTwo');
      } else {
        const convertedBalance=Number(dwollaWalletBalance);
        if(Number.isNaN(convertedBalance))return;
        if(convertedBalance===0){
          if(dwollaWalletPendingTransfer){
            modalState(secondaryBuy.PENDING_TRANSFER);
          }else{
            modalState(secondaryBuy.NO_MONEY);
          }
        }else{
          modalState('StepTwo')
        }
      }
    }
    setIsPinVerified(false);
  }, [bankExists, bankVerified, isPinVerified, dwollaWalletBalance, dwollaWalletPendingTransfer, userData, activeTab, skipEligibilityToast, isBuyEligible, isSellEligible, buyIneligibleMsg, sellIneligibleMsg]);

  const handleFeatchDeatilSuccessfully = () => {
    setPin(undefined);
    setIsPinVerified(true);
    setIsTurmAndCondistion(false);
    setNumberOfShare('');
    setLoading(false);
    setPartialFills(true);
    setDuration('Good Until Cancel');
    setLimitPrice();
  };

  const openTOSAgreementModal = () => {
    setIsTOSModal(true);
  };

  const onTOSAgreementSuccess = () => {
    setIsTOSModal(false);
    onPinVerifySuccess();
  };

  const onTOSModalClose = () => {
    setIsTOSModal(false);
  };

  const openPortfolioTOSModal = () => {
    setPortfolioTOSModal(true);
  };

  const onPortfolioTOSSuccess = () => {
    setPortfolioTOSModal(false);
    handleFeatchOpenOrderDetail(storeOrderValue);
  };

  const onPortfolioTOSClose = () => {
    setPortfolioTOSModal(false);
  };

  const handleAddBank = (value) => {
    let updatedWallet = walletsData?.filter((item) => item.id !== value && item.sourceType === 'Wallet');
    let updatedBank = walletsData?.filter((item) => item.id !== value && item.sourceType === 'Bank');
    setWalletsData([value.data, ...updatedWallet, ...updatedBank, { id: -1, brandName: 'Add new', sourceType: 'add new' }]);
    setSelectedPaymentMethod(value.data);
    isEditPayment(false);
  };
  const handleGetAccountBalances = async () => {
    try {
      const userData = await getAccountBalances();
      if (!userData.isError) {
        setUserBalance(userData?.data);
      }
    } catch (error) {}
  };
  const onPinVerifySuccess = async () => {
    handleFeatchBankDetail();
    Object.keys(propertyDetailData).length <= 0
      ? await dispatch(
          fetchPropertyDetailsAction({
            id: quickBuyPropertyData?.propertyId,
            onSuccess: () => handleFeatchDeatilSuccessfully(),
          })
        )
      : handleFeatchDeatilSuccessfully();
    handleGetAccountBalances();
  };

  const handleAfterAgreementAccepted = async () => {
    // Mark isSecondaryApplicationRequired as false immediately so the isPinVerified
    // useEffect does not re-open the AccessModal before navigating to Create Order.
    // Also set the flag so the eligibility toast is suppressed on this automatic
    // re-entry — the user will see it only when they next explicitly click Buy/Sell.
    skipEligibilityOnce.current = true;
    dispatch(addUserData({ userData: { ...userData, isSecondaryApplicationRequired: false } }));
    onPinVerifySuccess();
  };

  const handleVerifyPin = async (isTOSAccepted = false) => {
    if (!isTOSAccepted) {
      modalState('');
      toastNotify(
        'newAgreement',
        <ListingAgreementNotification
          onBtnClick={() => {
            notification.close('newAgreement');
            openTOSAgreementModal();
          }}
        />
      );
    } else {
      onPinVerifySuccess();
    }
  };

  const handleVerifyPinOpenOrders = async (isTOSAccepted = false) => {
    if (!isTOSAccepted) {
      setOpenPinModal(false);
      toastNotify(
        'newAgreement',
        <ListingAgreementNotification
          onBtnClick={() => {
            notification.close('newAgreement');
            openPortfolioTOSModal();
          }}
        />
      );
    } else {
      setOpenPinModal(false);
      handleFeatchOpenOrderDetail(storeOrderValue);
    }
  };

  const handleFeatchOpenOrderDetail = async (selectedProperty) => {
    await dispatch(updatePropertyDetails({}));
    setSelectedProperty(selectedProperty);
    setOpenModal(true);
    await dispatch(
      fetchPropertyDetailsAction({
        id: selectedProperty?.propertyId,
        orderId: selectedProperty?.openOrderId,
      })
    );
  };

  /**
   * Opens the PIN entry step for a BUY order.
   * Eligibility is checked after PIN is verified.
   */
  const openForBuy = () => {
    setActiveTab('1');
    modalState('StepOne');
  };

  /**
   * Opens the PIN entry step for a SELL order.
   * Eligibility is checked after PIN is verified.
   */
  const openForSell = () => {
    setActiveTab('2');
    modalState('StepOne');
  };

  const handleForgotPin = async () => {
    setLoading(true);
    try {
      const resData = await resetLoginPin();
      if (!resData.isError) {
        setOpenPinModal(false);
        toastNotify('success', resData?.message);
        modalState();
        setResetPin(true);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceBid = async () => {
    if (Number(totalPurchase) < userBalance?.cash) {
      const bidData = {
        propertyId: quickBuyPropertyData?.propertyId?.toString(),
        fundingSourceId: selectedPaymentMethod?.id?.toString(),
        noOfShares: Number(numberOfShare),
        pricePerShare: Number(limitPrice),
        partialFills: partialFills,
        duration: duration,
      };
      await dispatch(seondaryPlaceBidOrder({ bidData, onSuccess: () => modalState('success') }));
    } else {
      const linkText = 'My Account>Account Balances';
      const linkPath = '/account/transactions';
      const openLink = () => {
        window.location.href = linkPath;
      };
      notification.error({
        description: (
          <div>
            <span style={{ cursor: 'pointer' }} onClick={openLink}>
              Please go to {<a href={linkPath}>{linkText}</a>} to transfer funds into your account.
            </span>
          </div>
        ),
        duration: 5,
      });
    }
  };

  const handleSellOrder = async () => {
    const bidData = {
      propertyId: quickBuyPropertyData?.propertyId?.toString(),
      fundingSourceId: selectedPaymentMethod?.id?.toString(),
      noOfShares: Number(numberOfShare),
      pricePerShare: Number(limitPrice),
      partialFills: partialFills,
      duration: duration,
    };
    await dispatch(secondarySellOrderBid({ bidData, onSuccess: () => modalState('success') }));
  };

  return {
    loading,
    pin,
    resetPin,
    numberOfShare,
    isMaxRangeError,
    isMinRangeError,
    minButtonValue,
    sharesRemainingToPurchase,
    sharesRemainingToPurchaseForSecondary,
    limitPrice,
    LowestAskPrice,
    highestBid,
    duration,
    totalInvestment,
    transactionFee,
    totalPurchase,
    isEditPayment,
    walletsData,
    selectedPaymentMethod,
    isTurmAndCondistion,
    partialFills,
    perSharePrice,
    userPurchasedShare,
    activeTab,
    propertyDetails,
    userBalance,
    selectedProperty,
    openModal,
    openPinModal,
    isTOSModal,
    setActiveTab,
    setPartialFills,
    setPin,
    handleVerifyPin,
    handleForgotPin,
    setResetPin,
    setNumberOfShare,
    setLimitPrice,
    setDuration,
    setIsEditPayment,
    handleSelectPaymentMethod,
    handleAddBank,
    setIsTurmAndCondistion,
    handlePlaceBid,
    handleSellOrder,
    handleFeatchOrderDetail,
    setSelectedProperty,
    setOpenModal,
    handleFeatchOpenOrderDetail,
    setStoreOrderValue,
    handleVerifyPinOpenOrders,
    setOpenPinModal,
    setLoading,
    onTOSAgreementSuccess,
    onTOSModalClose,
    portfolioTOSModal,
    onPortfolioTOSSuccess,
    onPortfolioTOSClose,
    onPinVerifySuccess,
    handleAfterAgreementAccepted,
    openForBuy,
    openForSell,
  };
};

export default useQuickBuySecondary;
